import { type FC, useEffect, useState } from "react";
import Modal from "~/common/components/Modal";
import Spinner from "~/common/components/Spinner/Spinner";
import { Alert, Button, Stack, TextField, styled } from "~/common/mui";
import { type CreditCard } from "./manageCreditCard";

import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { CC_MIN_JS_FILE_URL } from "~/utils/constants";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";

interface addModal {
  closeModal: () => void;
  updateCard: (data: CreditCard) => void;
  defaultCC: boolean;
}

const AddCreditCard: FC<addModal> = (props) => {
  const { t } = useTranslation();
  const { closeModal, updateCard, defaultCC } = props;

  const [data, setData] = useState<any>(null);
  const [errors, setErrors] = useState<any>(null);
  const [isWindowPrimedWithFunctions, setPrimed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cardOk, setIsCardOk] = useState(false);
  const [addedCard, setAddedCard] = useState<CreditCard>();
  const [ccDescription, setCcDescription] = useState<string>("");
  const [errorDescription, setErrorDescription] = useState(false);
  const [duplicated, setDuplicated] = useState(false);
  const [defaultCard, setDefaultCard] = useState(false);
  const [saveForLater, setSaveForLater] = useState(false);
  const [isLoadingAddCard, setIsLoadingAddCard] = useState(false);
  const [showCVVMessage, setShowCVVMessage] = useState(false);

  const [iframeUrl, setIframeUrl] = useState("");

  useEffect(() => {
    if (iframeUrl) {
      (window as any).completePayment = setData;
      (window as any).handlePaymentErrors = setErrors;
      (window as any).cancelPayment = () => closeModal();
      (window as any).whatCVV2 = () => setShowCVVMessage(true);
      setPrimed(true);
    }
  }, [iframeUrl]);

  const getCcIframe = () => {
    setIsLoading(true);
    fetch("get-cc-iframe", { credentials: "same-origin", mode: "cors" })
      .then((res) => res.json())
      .then((json) => {
        setIframeUrl(json);
        setIsLoading(false);
      })
      .catch(function () {
        setIsLoading(false);
      });
  };

  const getNewCcIframe = () => {
    setData(null);
    setErrors(null);
    setIsCardOk(false);
    setAddedCard(undefined);
    setCcDescription("");
    setErrorDescription(false);
    setDuplicated(false);
    getCcIframe();
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.async = true;
    script.src = CC_MIN_JS_FILE_URL;
    document.body.appendChild(script);
    getCcIframe();

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (data && data.code === "000" && data.uID) {
      (async () => {
        const response = await fetch(`get-new-cc?uid=${data.uID}`, { credentials: "same-origin", mode: "cors" });
        try {
          const json = await response.json();
          setAddedCard(json);
          setIsCardOk(true);
          setIsLoading(false);
        } catch {
          getNewCcIframe();
          setErrors({ errorCode: ["XXX"] });
        }
      })();
    }
  }, [data]);

  const handleAddCard = () => {
    if ((defaultCard || saveForLater) && ccDescription === "") {
      setErrorDescription(true);
    } else {
      setErrorDescription(false);
      if (addedCard) {
        if (defaultCard || saveForLater) {
          setIsLoadingAddCard(true);
          fetch(`update-cc?uid=${addedCard?.uid}&name=${ccDescription}&defaultCard=${defaultCard}`, {
            credentials: "same-origin",
            mode: "cors",
          })
            .then((res) => res.json())
            .then((json) => {
              setIsLoadingAddCard(false);
              if (!json.uid) {
                setDuplicated(true);
              } else {
                if (defaultCC) {
                  if (json?.defaultCard) {
                    updateCard(json);
                    closeModal();
                  } else {
                    closeModal();
                  }
                } else {
                  updateCard(json);
                  closeModal();
                }
              }
            })
            .catch(function () {
              setIsLoadingAddCard(false);
            });
        } else {
          updateCard(addedCard);
          closeModal();
        }
      }
    }
  };

  if (!iframeUrl || !isWindowPrimedWithFunctions) {
    return null;
  }

  return (
    <Modal
      onClose={closeModal}
      title={t("checkoutPage.addCreditCard.title")}
      footerChildren={
        data && (
          <Stack direction={"row"} gap={1}>
            {duplicated && (
              <Button onClick={getNewCcIframe} variant={"text"}>
                {t("checkoutPage.addCreditCard.addNew")}
              </Button>
            )}
            <Button onClick={() => closeModal()} variant={"text"}>
              {t("checkoutPage.addCreditCard.btnCancel")}
            </Button>
            {!duplicated && <Button onClick={() => handleAddCard()}>{t("checkoutPage.addCreditCard.btnApply")}</Button>}
          </Stack>
        )
      }
    >
      {isLoading && <Spinner />}
      {showCVVMessage && (
        <div className={"d-flex mb-2"}>
          <Alert data-test-id={""} severity="info" className="label-large">
            {t("checkoutPage.addCreditCard.whatsCVV")}
          </Alert>
          <CloseCvcButton onClick={() => setShowCVVMessage(false)}>
            <CloseIcon  />
          </CloseCvcButton>
        </div>
      )}
      {!data && !isLoading && (
        <div className={"d-flex flex-direction-column"}>
          {errors?.errorCode &&
            errors.errorCode.length > 0 &&
            errors.errorCode.map((error: string, index: any) => (
              <>
                {error !== "" && (
                  <Alert
                    key={String(index)}
                    data-test-id={"error-message-" + index}
                    severity="error"
                    className="mb-2 label-large"
                  >
                    {error === "200" && <>{t("checkoutPage.addCreditCard.errorCardName")}</>}
                    {(error === "330" || error === "340" || error === "370") && (
                      <>{t("checkoutPage.addCreditCard.errorCardDate")}</>
                    )}
                    {error === "355" && <>{t("checkoutPage.addCreditCard.errorCardCVC")}</>}
                    {error !== "355" && error !== "330" && error !== "340" && error !== "370" && error !== "200" && (
                      <>{t("checkoutPage.addCreditCard.errorCardGeneral")}</>
                    )}
                  </Alert>
                )}
              </>
            ))}
          {iframeUrl && <CcIframe className={"scha__checkout__iframe-add-credit-card"} src={iframeUrl} />}
        </div>
      )}
      {data && cardOk && !isLoading && (
        <>
          <h2 className="label-large mt-0 mb-2">{t("checkoutPage.addCreditCard.titleAuthorized")}</h2>
          <div
            className={
              "d-flex  mb-2 flex-direction-column scha__order-summary-manage-credit-card-item" +
              (errorDescription ? "__error" : "")
            }
          >
            <div className={"d-flex flex-direction-column"}>
              <TextField
                fullWidth
                inputProps={{ "data-test-id": "" }}
                label={t("checkoutPage.addCreditCard.input.placeholder")}
                value={ccDescription}
                onChange={(e) => setCcDescription(e.target.value)}
                error={errorDescription}
                disabled={!defaultCard && !saveForLater}
              />
              <div className={"d-flex"}>
                <div
                  className={"d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"}
                >
                  {t("checkoutPage.addCreditCard.nameCard")}
                  {addedCard?.cardHolderName && <p>{addedCard.cardHolderName}</p>}
                </div>
                <div
                  className={"d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"}
                >
                  {t("checkoutPage.addCreditCard.ccnumber")}
                  {addedCard?.number && <p>**** **** **** {addedCard.number}</p>}
                </div>
                <div
                  className={"d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"}
                >
                  {t("checkoutPage.addCreditCard.expDate")}
                  {addedCard?.expMonth && addedCard?.expYear && (
                    <p>
                      {addedCard.expMonth}/{addedCard.expYear}
                    </p>
                  )}
                </div>
                <div
                  className={"d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"}
                >
                  {t("checkoutPage.addCreditCard.cardType")}
                  {addedCard?.type && <p className={"text-uppercase"}>{addedCard.type}</p>}
                </div>
              </div>
            </div>
          </div>
          {errorDescription && (
            <Alert severity="error" className="mb-4 label-large">
              {t("checkoutPage.addCreditCard.errorMissingDescription")}
            </Alert>
          )}
          {duplicated && (
            <Alert severity="error" className="mb-4 label-large">
              {t("checkoutPage.addCreditCard.cardDuplicated")}
            </Alert>
          )}
          {!errorDescription && !duplicated && (
            <Alert severity="info" className="mb-4 label-large">
              {t("checkoutPage.addCreditCard.cardAuthorized")}
            </Alert>
          )}
          <div
            className={"d-flex flex-direction-column mb-4 scha__order-summary-manage-credit-card-item__default-options"}
          >
            <CheckboxWithLabel
              data-test-id={""}
              checked={defaultCard}
              onCheck={() => {
                setDefaultCard(!defaultCard);
                setSaveForLater(true);
              }}
            >
              {t("checkoutPage.addCreditCard.setDefault")}
            </CheckboxWithLabel>
            <CheckboxWithLabel data-test-id={""} checked={saveForLater} onCheck={() => setSaveForLater(!saveForLater)}>
              {t("checkoutPage.addCreditCard.saveLater")}
            </CheckboxWithLabel>
          </div>
          {isLoadingAddCard && <Spinner />}
        </>
      )}
    </Modal>
  );
};

const CloseCvcButton = styled("div")(({ theme }) => ({
  color: theme.palette.info.main,
  "&:hover": {
    cursor: "pointer",
  },
}));

const CcIframe = styled("iframe")({});

export default AddCreditCard;
